import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import logo from "@/assets/quibblies-logo.png";
import {
  AlarmClock,
  Bell,

  BookMarked,
  Compass,
  Globe2,
  Hash,
  Inbox,
  LayoutGrid,
  LogOut,
  Menu,
  PenLine,
  Search,
  Settings,
  Shield,
  Sparkles,
  User as UserIcon,
  UserCheck,
  Users,
  Image as ImageIcon,
  MailCheck,
  FileText,
  ChartLine,
  ScrollText,
  X,

} from "lucide-react";

import { AdminNavMenu, visibleAdminGroups } from "@/components/admin-nav-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth-context";
import { fetchUnreadNotificationCount } from "@/lib/queries";
import { cn } from "@/lib/utils";

const PUBLIC_NAV = [
  { to: "/discover", label: "Discover", icon: Compass },
  { to: "/categories", label: "Categories", icon: LayoutGrid },
  { to: "/about", label: "About", icon: Sparkles },
] as const;

const MEMBER_NAV = [
  { to: "/discover", label: "Discover", icon: Compass },
  { to: "/categories", label: "Categories", icon: LayoutGrid },
  { to: "/following", label: "Following", icon: Users },
  { to: "/library", label: "Library", icon: BookMarked },
  { to: "/messages", label: "Messages", icon: Inbox },
] as const;


export function SiteHeader() {
  const { user, profile, isStaff, isWriter, isAdmin, isOwner, signOut } = useAuth();
  const nav = user ? MEMBER_NAV : PUBLIC_NAV;

  const adminGroups = visibleAdminGroups({ isStaff, isAdmin, isOwner });

  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const { data: unread = 0 } = useQuery({
    queryKey: ["unread-notifications", user?.id],
    queryFn: () => fetchUnreadNotificationCount(user!.id),
    enabled: Boolean(user?.id),
  });

  function submitSearch(event: React.FormEvent) {
    event.preventDefault();
    void navigate({ to: "/discover", search: { q: search || undefined } });
    setMobileOpen(false);
  }

  async function handleSignOut() {
    await signOut();
    void navigate({ to: "/", replace: true });
  }

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2" aria-label="Quibblies home">
          <img
            src={logo}
            alt="Quibblies"
            width={816}
            height={816}
            className="h-9 w-9 object-contain"
          />
          <span className="font-display text-xl tracking-tight text-foreground">Quibblies</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-full px-3 py-2 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
              activeProps={{ className: "bg-accent text-foreground" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden max-w-xs flex-1 md:block">
          <label className="sr-only" htmlFor="site-search">
            Search publications
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="site-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search publications or people"
              className="h-10 rounded-full border-border/70 bg-surface/70 pl-9"
            />
          </div>
        </form>

        <div className="ml-auto flex items-center gap-2 md:ml-0">
          {user ? (
            <>
              {isWriter ? (
                <>
                  <Button asChild variant="ghost" size="pill" className="hidden lg:inline-flex">
                    <Link to="/studio">
                      <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                      Studio
                    </Link>
                  </Button>
                  <Button asChild variant="hero" size="pill" className="hidden sm:inline-flex">
                    <Link to="/write">
                      <PenLine className="h-4 w-4" aria-hidden="true" />
                      Write
                    </Link>
                  </Button>
                </>
              ) : (
                <Button asChild variant="hero" size="pill" className="hidden sm:inline-flex">
                  <Link to="/start-writing">
                    <PenLine className="h-4 w-4" aria-hidden="true" />
                    Start writing
                  </Link>
                </Button>
              )}


              <AdminNavMenu isStaff={isStaff} isAdmin={isAdmin} isOwner={isOwner} />



              <Button asChild variant="ghost" size="icon" className="relative">
                <Link to="/messages" aria-label={`Messages (${unread} unread)`}>
                  <Bell className="h-4 w-4" aria-hidden="true" />
                  {unread > 0 ? (
                    <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  ) : null}
                </Link>
              </Button>

              <Button asChild variant="ghost" size="icon" aria-label="Account settings">
                <Link to="/settings/profile-images">
                  <Settings className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>

              <Button asChild variant="ghost" size="icon" aria-label="Language and region">
                <Link to="/settings/region">
                  <Globe2 className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>


              <Link
                to="/u/$username"
                params={{ username: profile?.username ?? "" }}
                className="hidden h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-border bg-accent text-xs font-semibold text-accent-foreground transition-smooth hover:border-primary sm:flex"
                aria-label="Your profile"
              >
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  (profile?.display_name ?? "Q").slice(0, 1).toUpperCase()
                )}
              </Link>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleSignOut}
                aria-label="Sign out"
                className="hidden sm:inline-flex"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </Button>
            </>
          ) : (
            <>
              <Button asChild variant="ghost" size="pill" className="hidden sm:inline-flex">
                <Link to="/auth">Sign in</Link>
              </Button>
              <Button asChild variant="hero" size="pill">
                <Link to="/auth" search={{ mode: "register" }}>
                  Join Quibblies
                </Link>
              </Button>
            </>
          )}

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((open) => !open)}
          >
            {mobileOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </Button>
        </div>
      </div>

      <div
        className={cn(
          "overflow-hidden border-t border-border/60 bg-surface/95 lg:hidden",
          mobileOpen ? "max-h-[80vh] overflow-y-auto" : "max-h-0 border-t-0",
        )}
        style={{ transition: "max-height 300ms cubic-bezier(0.22,1,0.36,1)" }}
      >
        <div className="space-y-2 px-4 py-4">
          <form onSubmit={submitSearch} className="md:hidden">
            <label className="sr-only" htmlFor="mobile-search">
              Search publications
            </label>
            <Input
              id="mobile-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search publications or people"
              className="h-10 rounded-full bg-background"
            />
          </form>
          {nav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 rounded-2xl px-3 py-3 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
              activeProps={{ className: "bg-accent text-foreground" }}
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
              {item.label}
            </Link>
          ))}
          {user ? (
            <>
              {isWriter ? (
                <>
                  <Link
                    to="/studio"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 rounded-2xl px-3 py-3 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
                  >
                    <LayoutGrid className="h-4 w-4" aria-hidden="true" />
                    Creator Studio
                  </Link>
                  <Link
                    to="/write"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 rounded-2xl px-3 py-3 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
                  >
                    <PenLine className="h-4 w-4" aria-hidden="true" />
                    Write
                  </Link>
                </>
              ) : (
                <Link
                  to="/start-writing"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 rounded-2xl px-3 py-3 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
                >
                  <PenLine className="h-4 w-4" aria-hidden="true" />
                  Start writing
                </Link>
              )}

              {adminGroups.map((group) => (
                <div key={group.heading} className="pt-2">
                  <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
                    {group.heading}
                  </p>
                  {group.links.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 rounded-2xl px-3 py-3 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
                    >
                      <link.icon className="h-4 w-4" aria-hidden="true" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              ))}

              <Link
                to="/u/$username"
                params={{ username: profile?.username ?? "" }}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 rounded-2xl px-3 py-3 text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
              >
                <UserIcon className="h-4 w-4" aria-hidden="true" />
                Your profile
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm text-muted-foreground transition-smooth hover:bg-accent hover:text-foreground"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </button>
            </>
          ) : (
            <Button asChild variant="hero" className="w-full" size="pill">
              <Link to="/auth" onClick={() => setMobileOpen(false)}>
                Sign in or join
              </Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
